import { createContext, useContext, useState, useCallback, useRef, useEffect, type ReactNode, type Dispatch, type SetStateAction } from "react";
import { getAuthHeaders } from "@/lib/api";

const API_BASE = import.meta.env.VITE_API_URL ?? "";

export interface MealPlanRecipe {
  steps: string[];
  prepMinutes: number;
  cookMinutes: number;
  tip?: string;
}

export interface MealPlanItem {
  foodName: string;
  category: string;
  portionG: number;
  portionDescription: string;
  platePercent?: number;
  densityLevel?: string;
  cookingMethod?: string;
  nutrients: { kcal: number; protein: number; carbs: number; fat: number; fiber: number };
  recipe?: MealPlanRecipe;
}

export interface MealPlanMeal {
  mealSlot: string;
  items: MealPlanItem[];
  totalKcal: number;
}

export interface MealPlanDay {
  dayLabel: string;
  meals: MealPlanMeal[];
  _failed?: boolean;
}

export interface MealPlanResult {
  /** Server id for the member's stored week plan. The grocery tap sends this, not the meals. */
  id?: string;
  days: (MealPlanDay | null)[];
  notes: string;
  totalDailyKcal?: number;
}

export interface GenerateOpts {
  scope: "meal" | "day" | "week";
  source: "plate-log" | "dashboard";
  useOnlyUserFoods?: boolean;
  startDate?: string;
  cookingMethods?: string[];
  includeRecipes?: boolean;
  useInternalDb?: boolean;
}

interface MealPlanState {
  generating: boolean;
  plan: MealPlanResult | null;
  error: string | null;
  scope: "meal" | "day" | "week";
  source: "plate-log" | "dashboard" | null;
  completedDays: number;
  totalDays: number;
  queueDepth: number;
}

interface MealPlanContextValue {
  state: MealPlanState;
  generate: (opts: GenerateOpts) => void;
  clearPlan: () => void;
  setScope: (scope: "meal" | "day" | "week") => void;
  cancelAndClearQueue: () => void;
  /** Replace the visible plan with one restored from the account workflow. */
  restorePlan: (plan: MealPlanResult | null) => void;
}

const MealPlanContext = createContext<MealPlanContextValue | null>(null);

const INITIAL_STATE: MealPlanState = {
  generating: false,
  plan: null,
  error: null,
  scope: "day",
  source: null,
  completedDays: 0,
  totalDays: 0,
  queueDepth: 0,
};

export function MealPlanProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<MealPlanState>(INITIAL_STATE);

  const abortRef = useRef<AbortController | null>(null);
  const queueRef = useRef<GenerateOpts[]>([]);
  const processingRef = useRef(false);
  const genIdRef = useRef(0);

  const runGenerate = useCallback(async (opts: GenerateOpts) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const myGenId = ++genIdRef.current;
    const isWeek = opts.scope === "week";

    setState(prev => ({
      ...prev,
      generating: true,
      plan: isWeek
        ? { days: new Array(7).fill(null) as MealPlanDay[], notes: "", totalDailyKcal: undefined }
        : null,
      error: null,
      scope: opts.scope,
      source: opts.source,
      completedDays: 0,
      totalDays: isWeek ? 7 : 0,
      queueDepth: queueRef.current.length,
    }));

    try {
      const authHeaders = await getAuthHeaders();

      if (isWeek) {
        await runWeekStream(opts, authHeaders, controller.signal, setState);
      } else {
        await runStandard(opts, authHeaders, controller.signal, setState);
      }
    } catch (err: any) {
      if (err.name === "AbortError") return;
      if (myGenId !== genIdRef.current) return;
      setState(prev => ({ ...prev, generating: false, error: err.message ?? "Generation failed" }));
    } finally {
      if (myGenId !== genIdRef.current) return;
      processingRef.current = false;
      const next = queueRef.current.shift();
      if (next) {
        setState(prev => ({ ...prev, queueDepth: queueRef.current.length }));
        processingRef.current = true;
        runGenerate(next);
      }
    }
  }, []);

  const generate = useCallback((opts: GenerateOpts) => {
    if (processingRef.current) {
      queueRef.current.push(opts);
      setState(prev => ({ ...prev, queueDepth: queueRef.current.length }));
      return;
    }
    processingRef.current = true;
    runGenerate(opts);
  }, [runGenerate]);

  const clearPlan = useCallback(() => {
    setState(prev => ({ ...prev, plan: null, error: null }));
  }, []);

  const setScope = useCallback((scope: "meal" | "day" | "week") => {
    setState(prev => ({ ...prev, scope }));
  }, []);

  const cancelAndClearQueue = useCallback(() => {
    genIdRef.current++;
    queueRef.current = [];
    processingRef.current = false;
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setState(prev => ({
      ...prev,
      generating: false,
      queueDepth: 0,
    }));
  }, []);

  const restorePlan = useCallback((plan: MealPlanResult | null) => {
    setState(prev => prev.generating ? prev : { ...prev, plan, error: null });
  }, []);

  // Keyed by account: on unmount, stop in-flight generation so it never writes into another account.
  useEffect(() => () => {
    genIdRef.current++;
    queueRef.current = [];
    abortRef.current?.abort();
  }, []);

  return (
    <MealPlanContext.Provider value={{ state, generate, clearPlan, setScope, cancelAndClearQueue, restorePlan }}>
      {children}
    </MealPlanContext.Provider>
  );
}

async function runStandard(
  opts: GenerateOpts,
  authHeaders: Record<string, string>,
  signal: AbortSignal,
  setState: Dispatch<SetStateAction<MealPlanState>>,
) {
  const res = await fetch(`${API_BASE}/api/neural/meal-plan`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders },
    credentials: "include",
    body: JSON.stringify({
      scope: opts.scope,
      useOnlyUserFoods: opts.useInternalDb ? false : (opts.useOnlyUserFoods ?? (opts.scope !== "week")),
      ...(opts.startDate ? { startDate: opts.startDate } : {}),
      ...(opts.cookingMethods?.length ? { cookingMethods: opts.cookingMethods } : {}),
      ...(opts.includeRecipes ? { includeRecipes: true } : {}),
      ...(opts.useInternalDb ? { useInternalDb: true } : {}),
    }),
    signal,
  });
  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    throw new Error(errData.error ?? "Failed to generate meal plan");
  }
  const data = await res.json();
  setState(prev => ({ ...prev, generating: false, plan: data.plan, error: null, completedDays: 0, totalDays: 0 }));
}

async function runWeekStream(
  opts: GenerateOpts,
  authHeaders: Record<string, string>,
  signal: AbortSignal,
  setState: Dispatch<SetStateAction<MealPlanState>>,
) {
  const res = await fetch(`${API_BASE}/api/neural/meal-plan/week-stream`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders },
    credentials: "include",
    body: JSON.stringify({
      useOnlyUserFoods: opts.useInternalDb ? false : (opts.useOnlyUserFoods ?? false),
      ...(opts.startDate ? { startDate: opts.startDate } : {}),
      ...(opts.cookingMethods?.length ? { cookingMethods: opts.cookingMethods } : {}),
      ...(opts.includeRecipes ? { includeRecipes: true } : {}),
      ...(opts.useInternalDb ? { useInternalDb: true } : {}),
    }),
    signal,
  });

  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    throw new Error(errData.error ?? "Failed to start week plan generation");
  }

  if (!res.body) throw new Error("No response body for streaming");

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  const days: Array<{ dayLabel: string; meals: any[]; _failed?: boolean }> = new Array(7).fill(null);
  let gotDone = false;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() ?? "";
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed.startsWith("data: ")) continue;
      const raw = trimmed.slice(6).trim();
      if (!raw || raw === "[DONE]") continue;
      try {
        const event = JSON.parse(raw);
        if (event.type === "day") {
          const { dayIndex, day } = event;
          days[dayIndex] = day;
          const completedCount = days.filter(Boolean).length;
          setState(prev => ({
            ...prev,
            completedDays: completedCount,
            plan: {
              days: [...days] as any,
              notes: "",
              totalDailyKcal: prev.plan?.totalDailyKcal,
            },
          }));
        } else if (event.type === "done") {
          gotDone = true;
          const failedCount = (event.plan?.days ?? []).filter((d: any) => d?._failed).length;
          setState(prev => ({
            ...prev,
            generating: false,
            completedDays: 7,
            totalDays: 7,
            plan: event.plan,
            error: failedCount > 0
              ? `${failedCount} day${failedCount > 1 ? "s" : ""} could not be generated`
              : null,
          }));
        } else if (event.type === "error") {
          throw new Error(event.error ?? "Stream error");
        }
      } catch (parseErr: any) {
        if (parseErr?.message && !parseErr.message.includes("JSON")) {
          throw parseErr;
        }
      }
    }
  }

  if (!gotDone) {
    setState(prev => ({
      ...prev,
      generating: false,
      error: "Week plan generation ended unexpectedly — please try again",
    }));
  }
}

export function useMealPlanStore() {
  const ctx = useContext(MealPlanContext);
  if (!ctx) throw new Error("useMealPlanStore must be used within MealPlanProvider");
  return ctx;
}
